/**
 * The boot call — `GET /v1/me`.
 *
 * `docs/api-contract.md`: "User, orgs, workspaces, resolved entitlements. The
 * dashboard's boot call." One request, at mount, for everything the shell needs
 * to decide what to render.
 *
 * A 401 is not an error here — it is the ordinary state of a person whose
 * session expired in another tab, and the right response is to send them to
 * `/login`, not to render a red box. Anything else IS an error, and the shell
 * says so rather than rendering a nav over nothing.
 *
 * Story 3.8.
 */
import { useEffect, useState } from 'react'
import { apiGet, ApiFailure } from '../lib/api'

export type MeWorkspace = {
  id: string
  orgId: string
  name: string
  slug: string
  role: string
  canExport: boolean
  canPublish: boolean
  piiUnredacted: boolean
}

export type MeOrg = {
  id: string
  name: string
  slug: string
  role: string
  /**
   * Story 26.6 — the organisation's default consent wording, and its own sentence in the
   * AI disclosure. Both are words a respondent is shown, so the boot call carries them for
   * everybody: the builder seeds a new consent block from the first, and the Design tab's
   * preview draws the second. Absent from an older API, and absent reads as none.
   */
  consentCopy?: string | null
  disclosureText?: string | null
}

export type Entitlements = {
  plan: string
  flags: Record<string, boolean>
  limits: Record<string, number>
}

/**
 * Whether the AI layer can run, and the words for it when it cannot.
 *
 * THE COPY ARRIVES WITH THE STATE. `services/ai/availability.ts` owns those two
 * sentences so that the API, the dashboard and any later surface say the same
 * thing, and the Qualify tab renders what it is handed rather than holding a
 * second copy free to drift from the mockup. Nothing here rewords them, for the
 * reason `useMe`'s own error branch gives one field down.
 *
 * Optional because a `/v1/me` served by an API older than Story 18.8 carries no
 * such field, and absent has to read as "do not claim the layer is off" — a
 * banner shown on a working deployment is worse than no banner at all.
 *
 * Story 18.8.
 */
export type AiState =
  | { available: true }
  | {
      available: false
      reason: 'not_configured' | 'cap_reached' | 'switched_off' | 'provider_failed'
      notice: { headline: string; detail: string }
    }

export type Me = {
  user: {
    id: string
    email: string
    name: string
    emailVerified: boolean
    image: string | null
    /**
     * Better Auth's own column (Story 4.4). Optional because a session minted
     * before migration 0006 carries no such field, and absent means off.
     */
    twoFactorEnabled?: boolean
  }
  orgs: MeOrg[]
  workspaces: MeWorkspace[]
  entitlements: Record<string, Entitlements>
  ai?: AiState
  /**
   * The same answer per organisation — Story 23.5. An Owner who stopped all AI
   * spend is `switched_off` in that org and no other, so the Qualify tab reads its
   * own org's entry first and falls back to `ai`, the deployment's answer, for a
   * boot payload older than 23.5.
   */
  aiByOrg?: Record<string, AiState>
}

export type MeState =
  | { status: 'loading' }
  | { status: 'ready'; me: Me }
  | { status: 'signed-out' }
  | { status: 'error'; message: string }

export function useMe(): MeState {
  const [state, setState] = useState<MeState>({ status: 'loading' })

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const me = await apiGet<Me>('/v1/me')
        if (live) setState({ status: 'ready', me })
      } catch (error) {
        if (!live) return
        if (error instanceof ApiFailure && error.status === 401) {
          setState({ status: 'signed-out' })
          return
        }
        // The message NAMES THE FIX (ARCH §4.3) and is rendered as it arrives —
        // a client-side rewording is a message that can disagree with the server.
        setState({
          status: 'error',
          message:
            error instanceof ApiFailure
              ? error.message
              : "We couldn't reach Leadgible. Check your connection and reload.",
        })
      }
    })()
    return () => {
      live = false
    }
  }, [])

  return state
}
